import { useControls } from 'leva';
import { BoxHelper, Mesh } from 'three';

import { AccumulativeShadows, Center, Environment, OrbitControls, RandomizedLight, useHelper } from '@react-three/drei';
import { PresetsType } from '@react-three/drei/helpers/environment-assets';
import { Canvas } from '@react-three/fiber';
import * as React from 'react';

export type Props = {};

function Sphere() {
  const { roughness } = useControls({
    roughness: { value: 0, min: 0, max: 1 },
  });
  const refRoot = React.useRef<Mesh>(null!);
  useHelper(refRoot, BoxHelper);
  return (
    <Center top>
      <mesh ref={refRoot} castShadow>
        <sphereGeometry args={[0.75, 64, 64]} />
        <meshStandardMaterial metalness={1} roughness={roughness} />
      </mesh>
    </Center>
  );
}

function Env() {
  const [preset, setPreset] = React.useState<PresetsType>('forest');
  const [_, startTransition] = React.useTransition();
  const { blur } = useControls({
    blur: { value: 0.65, min: 0, max: 1 },
    preset: {
      value: preset,
      options: ['sunset', 'dawn', 'night', 'warehouse', 'forest', 'apartment', 'studio', 'city', 'park', 'lobby'],
      // If onChange is present the value will not be reactive, see https://github.com/pmndrs/leva/blob/main/docs/advanced/controlled-inputs.md#onchange
      // Instead we transition the preset value, which will prevents the suspense bound from triggering its fallback
      // That way we can hang onto the current environment until the new one has finished loading ...
      onChange: (value) => startTransition(() => setPreset(value)),
    },
  });
  return <Environment preset={preset} blur={blur} />;
}

function App01(props: Props) {
  const {} = props;

  return (
    <Canvas style={{ height: '400px' }} shadows camera={{ position: [0, 0, 4.5], fov: 50 }}>
      <group position={[0, -0.65, 0]}>
        <Sphere />
        <AccumulativeShadows
          temporal
          frames={200}
          color="purple"
          colorBlend={0.5}
          opacity={1}
          scale={10}
          alphaTest={0.85}
        >
          <RandomizedLight amount={8} radius={5} ambient={0.5} position={[5, 3, 2]} bias={0.001} />
        </AccumulativeShadows>
      </group>
      <Env />
      <OrbitControls
        autoRotate
        autoRotateSpeed={4}
        enablePan={false}
        enableZoom={false}
        minPolarAngle={Math.PI / 2.1}
        maxPolarAngle={Math.PI / 2.1}
      />
    </Canvas>
  );
}

export default React.memo(App01);
